/**
 * Single source of truth for the templates gallery layout.
 *
 * A responsive CSS grid of gallery cards: fixed 9:16 artwork box with a
 * metadata footer below (brand logo, name, remix metric) — App Store /
 * Apple Music gallery language: generous gutters, extra vertical rhythm
 * for the footer, a centered max-width container. Every artwork box
 * occupies the same pixel box per breakpoint, so rows align and the
 * `next/image` `sizes` attribute below stays accurate.
 */

/**
 * Shared centered container (width cap + gutters) for BOTH rails of the
 * gallery — the sticky filter row and the grid — so their left edges
 * can never drift apart.
 */
export const TEMPLATE_CONTAINER_CLASSES =
  'mx-auto w-full max-w-[1720px] px-4 md:px-8'

/**
 * No top padding: the sticky filter row's own bottom padding is the gap
 * between the pills and the first row, and it equals the gap between the
 * search bar and the pills — one rhythm from the search bar down.
 */
export const TEMPLATE_GRID_CLASSES = `${TEMPLATE_CONTAINER_CLASSES} mt-0 grid grid-cols-2 gap-4 pt-0 pb-6 md:gap-6 md:pb-8 lg:grid-cols-3 xl:grid-cols-4 xl:gap-8 2xl:grid-cols-5`

/**
 * Outer clickable card: a flex column of artwork box + metadata footer.
 * Pair with `TEMPLATE_GRID_CLASSES` on the parent.
 */
export const TEMPLATE_CARD_CLASSES =
  'group flex w-full min-w-0 flex-col text-left'

/**
 * The artwork box — aspect + chrome shared by every card in the grid
 * (and every skeleton). The 9:16 box is also the CLIP for `CardIframe`'s
 * fallback frame in `template-card.tsx`: the shared `ScaledEmailHtmlFrame`
 * fills the box's width at the email's design width and whatever runs
 * below the fold is cut here — change the aspect and the fallback crops
 * differently.
 */
export const TEMPLATE_CARD_PREVIEW_CLASSES =
  'relative aspect-[9/16] w-full overflow-hidden rounded-lg border border-border bg-card/10 shadow-sm'

/**
 * `sizes` attribute matching the column counts in `TEMPLATE_GRID_CLASSES`.
 * Keep in sync if breakpoints change (2 → 3@lg → 4@xl → 5@2xl).
 * The terminal fixed term reflects the `max-w-[1720px]` container cap:
 * (1720 − 2×32px gutters − 4×32px gaps) / 5 ≈ 305px — without it, a
 * viewport-relative tail keeps growing image fetches on wide screens.
 */
export const TEMPLATE_CARD_IMAGE_SIZES =
  '(max-width: 1024px) 50vw, (max-width: 1280px) 33vw, (max-width: 1536px) 25vw, (max-width: 1720px) 20vw, 310px'

/**
 * Flow cards (the Flows pill): a three-up strip of the first emails needs a
 * wider card than the 9:16 template card, so the collection has its own
 * column counts — 1 → 2@sm → 3@lg → 4@2xl — on the template grid's rhythm.
 */
export const FLOW_GRID_CLASSES = `${TEMPLATE_CONTAINER_CLASSES} mt-0 grid grid-cols-1 gap-4 pt-0 pb-6 sm:grid-cols-2 md:gap-6 md:pb-8 lg:grid-cols-3 xl:gap-8 2xl:grid-cols-4`

/**
 * A flow card is artwork over a caption, like the template card beside it:
 * the strip of emails, then the brand and the cadence. No chrome of its own.
 */
export const FLOW_CARD_CLASSES = 'group flex w-full min-w-0 flex-col text-left'

/** The strip: the first three emails side by side, same height, edge to edge. */
export const FLOW_CARD_STRIP_CLASSES = 'grid grid-cols-3 gap-2'

/** One email in the strip: the gallery's 9:16 portrait, top-cropped, full opacity. */
export const FLOW_CARD_STRIP_ITEM_CLASSES =
  'relative aspect-[9/16] overflow-hidden rounded-lg border border-border bg-card/10'

/**
 * `sizes` for a strip email: a third of a card at each column count. At the
 * 1720px container cap a card is ~390px, so an email is ~125px.
 */
export const FLOW_CARD_STRIP_SIZES =
  '(max-width: 640px) 30vw, (max-width: 1024px) 16vw, (max-width: 1536px) 11vw, 125px'
